import { useCallback, useEffect, useState } from 'react';

import { useBuckChatDrawer } from '../../../hooks/useBuckChatDrawer';
import type {
  BuckChatProps,
  ChatMessage,
  CurrentUser,
  LtPlusConfirmationKind,
  LtPlusCta,
  OtpContext,
} from '../../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../../utils/analytics';

type UseDrawerManagerFlowParams = {
  drawer: ReturnType<typeof useBuckChatDrawer>;
  listingId?: string;
  packageId?: string;
  currentUser?: CurrentUser;
  onAnalyticsEvent?: BuckChatProps['onAnalyticsEvent'];
  onAuthSuccess?: BuckChatProps['onAuthSuccess'];
  onLtPlusSuccess?: BuckChatProps['onLtPlusSuccess'];
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void;
};

export const useDrawerManagerFlow = ({
  drawer,
  listingId,
  packageId,
  currentUser,
  onAnalyticsEvent,
  onAuthSuccess,
  onLtPlusSuccess,
  updateMessage,
}: UseDrawerManagerFlowParams) => {
  const [isAwaitingLtPlusAuthResolution, setIsAwaitingLtPlusAuthResolution] = useState(false);

  const markMessageConfirmed = useCallback(
    (messageId?: string, ltPlusConfirmationKind?: LtPlusConfirmationKind) => {
      if (!messageId) {
        return;
      }

      updateMessage(messageId, {
        showConfirmation: true,
        content: undefined,
        ltPlusConfirmationKind,
      });
    },
    [updateMessage]
  );

  const handleSignupClick = useCallback(
    (msg?: ChatMessage) => {
      drawer.openSignup(undefined, msg);

      onAnalyticsEvent?.(BUCK_EVENTS.SIGNUP_DRAWER_OPENED, {
        [ANALYTICS_KEYS.LISTING_ID]: listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: packageId,
        [ANALYTICS_KEYS.MESSAGE_ID]: msg?.id,
      });
    },
    [drawer, onAnalyticsEvent, listingId, packageId]
  );

  const handleAuthSuccess = useCallback(
    (context?: OtpContext) => {
      const isReturningFromLtPlus = drawer.drawerState.ltPlusReturnFlowState?.isReturningToLtPlus;

      if (isReturningFromLtPlus) {
        if (!onAuthSuccess) {
          drawer.navigateToCheckout();
          return;
        }

        setIsAwaitingLtPlusAuthResolution(true);
        onAuthSuccess?.(context);
        return;
      }

      markMessageConfirmed(drawer.drawerState.signupMessage?.id);
      drawer.close();
      onAuthSuccess?.(context);
    },
    [drawer, markMessageConfirmed, onAuthSuccess]
  );

  useEffect(() => {
    if (!isAwaitingLtPlusAuthResolution || !currentUser?.isAuthenticated) {
      return;
    }

    if (currentUser.hasPremiumMembership) {
      markMessageConfirmed(drawer.drawerState.ltPlusMessageId, 'existing-member');
      drawer.close();
      onLtPlusSuccess?.();
    } else {
      drawer.navigateToCheckout();
    }

    setIsAwaitingLtPlusAuthResolution(false);
  }, [
    currentUser?.hasPremiumMembership,
    currentUser?.isAuthenticated,
    drawer,
    isAwaitingLtPlusAuthResolution,
    markMessageConfirmed,
    onLtPlusSuccess,
  ]);

  const handleSignupAbandoned = useCallback(
    (step: string) => {
      onAnalyticsEvent?.(BUCK_EVENTS.SIGNUP_ABANDONED, {
        [ANALYTICS_KEYS.STEP]: step,
        [ANALYTICS_KEYS.LISTING_ID]: listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: packageId,
      });
    },
    [onAnalyticsEvent, listingId, packageId]
  );

  const handleLtPlusCtaClick = useCallback(
    (ltPlusCta: LtPlusCta, messageId?: string) => {
      if (currentUser?.isAuthenticated && currentUser?.hasPremiumMembership && messageId) {
        markMessageConfirmed(messageId, 'existing-member');
        onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_EXISTING_MEMBER_CTA_CLICKED, {
          [ANALYTICS_KEYS.VARIANT]: ltPlusCta.variant,
          [ANALYTICS_KEYS.LISTING_ID]: listingId,
          [ANALYTICS_KEYS.PACKAGE_ID]: packageId,
          [ANALYTICS_KEYS.MESSAGE_ID]: messageId,
        });
        return;
      }

      drawer.openLtPlusBenefits(ltPlusCta, messageId);
      onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_DRAWER_OPENED, {
        [ANALYTICS_KEYS.VARIANT]: ltPlusCta.variant,
        [ANALYTICS_KEYS.LISTING_ID]: listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: packageId,
        [ANALYTICS_KEYS.MESSAGE_ID]: messageId,
      });
    },
    [
      currentUser?.hasPremiumMembership,
      currentUser?.isAuthenticated,
      drawer,
      listingId,
      onAnalyticsEvent,
      packageId,
      markMessageConfirmed,
    ]
  );

  const handleLtPlusDrawerClose = useCallback(() => {
    onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_DRAWER_CLOSED, {
      [ANALYTICS_KEYS.VARIANT]: drawer.drawerState.ltPlusCta?.variant,
      [ANALYTICS_KEYS.STEP]: drawer.drawerState.ltPlusInitialStep ?? 'benefits',
      [ANALYTICS_KEYS.MESSAGE_ID]: drawer.drawerState.ltPlusMessageId,
    });
    drawer.close();
  }, [drawer, onAnalyticsEvent]);

  const handleLtPlusSuccess = useCallback(() => {
    markMessageConfirmed(drawer.drawerState.ltPlusMessageId);
    drawer.close();
    onLtPlusSuccess?.();
  }, [drawer, markMessageConfirmed, onLtPlusSuccess]);

  const handleLtPlusSignInClick = useCallback(() => {
    onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_SIGN_IN_CLICKED, {
      [ANALYTICS_KEYS.VARIANT]: drawer.drawerState.ltPlusCta?.variant,
      [ANALYTICS_KEYS.MESSAGE_ID]: drawer.drawerState.ltPlusMessageId,
    });
    drawer.navigateToSignupFromLtPlus();
  }, [drawer, onAnalyticsEvent]);

  return {
    handleSignupClick,
    handleAuthSuccess,
    handleSignupAbandoned,
    handleLtPlusCtaClick,
    handleLtPlusDrawerClose,
    handleLtPlusSuccess,
    handleLtPlusSignInClick,
  };
};
